import { useEffect } from "react";
import {
  absoluteUrl,
  DEFAULT_LOCALE,
  localePath,
  OG_IMAGE_PATH,
  SITE_NAME,
  SITE_ORIGIN,
} from "@/config/site";
import {
  GOOGLE_PLAY_LISTING_URL,
  isExternalPlayStoreUrl,
} from "@/config/googlePlay";
import { useI18n } from "@/i18n/I18nContext";
import { LOCALES, type Locale } from "@/i18n/types";

type Props = {
  title: string;
  description: string;
  /** Path without locale, e.g. "" or "/faq" */
  path?: string;
  /** When true, ask crawlers not to index this document. */
  noIndex?: boolean;
};

function upsertMeta(
  selector: string,
  attrs: Record<string, string>,
  content?: string,
) {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value);
    }
    document.head.appendChild(el);
  }
  if (content !== undefined) el.setAttribute("content", content);
}

function upsertLink(
  rel: string,
  href: string,
  extra: Record<string, string> = {},
) {
  const extraKey = Object.entries(extra)
    .map(([k, v]) => `[${k}="${v}"]`)
    .join("");
  const selector = `link[rel="${rel}"]${extraKey}`;
  let el = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    for (const [key, value] of Object.entries(extra)) {
      el.setAttribute(key, value);
    }
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeNodes(selector: string) {
  document.head.querySelectorAll(selector).forEach((node) => node.remove());
}

function upsertJsonLd(id: string, data: Record<string, unknown>) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

const OG_LOCALE_MAP: Partial<Record<Locale, string>> = {
  en: "en_US",
  pt: "pt_BR",
  nb: "nb_NO",
};

function ogLocale(locale: Locale): string {
  return OG_LOCALE_MAP[locale] ?? `${locale}_${locale.toUpperCase()}`;
}

export function DocumentHead({
  title,
  description,
  path = "",
  noIndex = false,
}: Props) {
  const { locale } = useI18n();
  const pagePath = path === "/" ? "" : path;
  const canonicalPath = localePath(locale, pagePath);
  const canonical = absoluteUrl(canonicalPath);
  const ogImage = absoluteUrl(OG_IMAGE_PATH);

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = locale;

    upsertMeta('meta[name="description"]', { name: "description" }, description);

    if (noIndex) {
      upsertMeta('meta[name="robots"]', { name: "robots" }, "noindex, follow");
      removeNodes('link[rel="alternate"][hreflang]');
      removeNodes('link[rel="canonical"]');
    } else {
      const robots = document.head.querySelector('meta[name="robots"]');
      robots?.remove();
      upsertLink("canonical", canonical);

      removeNodes('link[rel="alternate"][hreflang]');
      for (const loc of LOCALES) {
        upsertLink("alternate", absoluteUrl(localePath(loc, pagePath)), {
          hreflang: loc,
        });
      }
      upsertLink(
        "alternate",
        absoluteUrl(localePath(DEFAULT_LOCALE, pagePath)),
        { hreflang: "x-default" },
      );
    }

    upsertMeta('meta[property="og:title"]', { property: "og:title" }, title);
    upsertMeta(
      'meta[property="og:description"]',
      { property: "og:description" },
      description,
    );
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, canonical);
    upsertMeta('meta[property="og:type"]', { property: "og:type" }, "website");
    upsertMeta(
      'meta[property="og:site_name"]',
      { property: "og:site_name" },
      SITE_NAME,
    );
    upsertMeta(
      'meta[property="og:locale"]',
      { property: "og:locale" },
      ogLocale(locale),
    );
    upsertMeta('meta[property="og:image"]', { property: "og:image" }, ogImage);

    removeNodes('meta[property="og:locale:alternate"]');
    for (const loc of LOCALES) {
      if (loc === locale) continue;
      const el = document.createElement("meta");
      el.setAttribute("property", "og:locale:alternate");
      el.setAttribute("content", ogLocale(loc));
      document.head.appendChild(el);
    }

    upsertMeta(
      'meta[name="twitter:card"]',
      { name: "twitter:card" },
      "summary_large_image",
    );
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, title);
    upsertMeta(
      'meta[name="twitter:description"]',
      { name: "twitter:description" },
      description,
    );
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, ogImage);

    const websiteLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_ORIGIN,
      inLanguage: locale,
    };

    const appLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: SITE_NAME,
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Android",
      description,
      url: absoluteUrl(localePath(DEFAULT_LOCALE)),
      image: ogImage,
    };

    if (isExternalPlayStoreUrl(GOOGLE_PLAY_LISTING_URL)) {
      appLd.downloadUrl = GOOGLE_PLAY_LISTING_URL;
      appLd.offers = {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      };
    }

    upsertJsonLd("ld-website", websiteLd);
    upsertJsonLd("ld-software", appLd);
  }, [title, description, pagePath, locale, canonical, ogImage, noIndex]);

  return null;
}
